import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkJCMD from '../lib/remark-jcmd'
import JCMDProcessor from './JCMDProcessor'

interface MarkdownRendererProps {
  content: string
  className?: string
  agentId?: string
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '', agentId }) => {
  return (
    <div className={`prose prose-sm max-w-none ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkJCMD]}
        components={{
          // 代码块样式 - 处理JCMD和普通代码块
          code: ({ className, children, node, meta, ...props }: any) => {
            const match = /language-(\w+)/.exec(className || '')
            const isInline = !match
            // 如果是JCMD代码块，优先处理
            let slicePos = 0
            if (match && match[1].toLowerCase() === 'jcmd') {
              var command = ''
              for(var i = 0; i < children.length; i++) { 
                if(children[i] === ' ') {
                  slicePos = i
                  break
                }
                command += children[i]
              }
              const paramsStr = children.slice(slicePos + 1)
              let params: any = {}
              try{
                params = JSON.parse(paramsStr)
              }catch(e){
                console.error('Error parsing JCMD params:', paramsStr)
                return (<pre className="bg-gray-100 rounded-lg p-4 overflow-x-auto">
                      <code className={className} {...props}>
                        指令读取中...
                      </code>
                    </pre>)
              }
              if ( command ) {
                try {
                  return (
                    <JCMDProcessor 
                      command={command} 
                      description={params.description} 
                      commandData={paramsStr}
                      alreadyCreated={!!agentId && command.toUpperCase() === 'CREATE'}
                    >
                      {children}
                    </JCMDProcessor>
                  )
                } catch (e) {
                  console.error('Error parsing JCMD meta:', e)
                  // 如果JCMD解析失败，回退到普通代码块显示
                  return (
                    <pre className="bg-gray-100 rounded-lg p-4 overflow-x-auto">
                      <code className={className} {...props}>
                        {children}
                      </code>
                    </pre>
                  )
                }
              }
            }
            
            // 普通代码块处理
            return !isInline ? (
              <pre className="bg-gray-100 rounded-lg p-4 overflow-x-auto">
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            ) : (
              <code className="bg-gray-100 px-1 py-0.5 rounded text-sm" {...props}>
                {children}
              </code>
            )
          },
          // 链接样式
          a: ({ href, children }: any) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              {children}
            </a>
          ),
          // 表格样式
          table: ({ children }: any) => (
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-gray-300">
                {children}
              </table>
            </div>
          ),
          th: ({ children }: any) => (
            <th className="border border-gray-300 px-4 py-2 bg-gray-100 font-semibold">
              {children}
            </th>
          ),
          td: ({ children }: any) => (
            <td className="border border-gray-300 px-4 py-2">
              {children}
            </td>
          ),
          // 列表样式
          ul: ({ children }: any) => (
            <ul className="list-disc list-inside space-y-1">
              {children}
            </ul>
          ),
          ol: ({ children }: any) => (
            <ol className="list-decimal list-inside space-y-1">
              {children}
            </ol>
          ),
          // 引用样式
          blockquote: ({ children }: any) => (
            <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600">
              {children}
            </blockquote>
          ),
          // 标题样式
          h1: ({ children }: any) => (
            <h1 className="text-xl font-bold mb-2">{children}</h1>
          ),
          h2: ({ children }: any) => (
            <h2 className="text-lg font-bold mb-2">{children}</h2>
          ),
          h3: ({ children }: any) => (
            <h3 className="text-base font-bold mb-1">{children}</h3>
          ),
          // 段落样式
          p: ({ children }: any) => (
            <p className="mb-2 last:mb-0">{children}</p>
          ),
          // 分割线样式
          hr: () => (
            <hr className="border-gray-300 my-4" />
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}

export default MarkdownRenderer 